/**
 * The shared halves of the campaign Locations section (#6182): the merged geo+radius row model,
 * its three-column sort, the business catalog/radius candidate sourcing for the LocationMapPicker,
 * and the flex-row table itself. Until now these ~170 lines were duplicated between Google's
 * `CampaignLocationsSection` and `MetaTargetingLocations` (a deliberate #5981 parity copy that
 * #6163 P3 flagged as mechanical drift risk).
 *
 * What deliberately does NOT live here: save semantics. Google edits a parent-owned draft that the
 * page save bar persists; Meta stages dialog edits and PATCHes immediately behind its geo-wipe
 * guards. Each platform keeps that logic beside its own dialog.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Pencil, Target, Trash2 } from 'lucide-react';

import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';
import { IconButton } from '@/components/glitch-ui/icon-button';
import type { RadiusFormValue } from '@/components/business/business-locations/radius-form';
import { businessLocationsQueryOptions, locationsByIdsQueryOptions } from '@/lib/query-options';
import { formatReach } from '@/lib/utils';
import type { CampaignProximityInput, Location } from '@/lib/types';

// One merged display row — geo locations and radius targets share a table (#5452), even though
// the backend keeps the geo and proximity fields separate on both platforms.
export type LocationTargetRow =
  | { kind: 'geo'; key: string; location: Location }
  | {
      kind: 'radius';
      key: string;
      index: number;
      address?: string;
      radiusKm: number;
      lat?: number | null;
      lng?: number | null;
    };

export type LocationTargetSortKey = 'location' | 'type' | 'reach';
export type LocationTargetSort = { key: LocationTargetSortKey; dir: 'asc' | 'desc' };

const formatCanonicalName = (name: string): string => name.replace(/,/g, ', ');

export const locationTargetRowLabel = (r: LocationTargetRow): string =>
  r.kind === 'geo' ? formatCanonicalName(r.location.canonical_name) : r.address || `${r.radiusKm} km radius`;

export const toProximityInput = (r: RadiusFormValue): CampaignProximityInput => ({
  latitude: r.lat,
  longitude: r.lng,
  radius_km: r.radiusKm,
  address: r.formattedAddress,
});

/**
 * Column sort over the merged rows. Type sort groups geo before radius (then by name); location
 * sort is alphabetical across both; reach sorts numerically with radius (no reach) sinking to the
 * bottom. null = the natural order (geo first, insertion order).
 */
export function useLocationTargetSort(rows: LocationTargetRow[]): {
  sortedRows: LocationTargetRow[];
  sort: LocationTargetSort | null;
  cycleSort: (key: LocationTargetSortKey) => void;
} {
  const [sort, setSort] = useState<LocationTargetSort | null>(null);

  // Cycle a column header: unsorted -> asc -> desc -> unsorted.
  const cycleSort = (key: LocationTargetSortKey) =>
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null));

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const labelOf = (r: LocationTargetRow) =>
      r.kind === 'geo' ? r.location.canonical_name : r.address || `${r.radiusKm} km radius`;
    const rank = (r: LocationTargetRow) => (r.kind === 'geo' ? 0 : 1);
    const reachOf = (r: LocationTargetRow) => (r.kind === 'geo' ? (r.location.reach ?? null) : null);
    return [...rows].sort((a, b) => {
      if (sort.key === 'reach') {
        const ra = reachOf(a);
        const rb = reachOf(b);
        if (ra == null && rb == null) return 0;
        if (ra == null) return 1;
        if (rb == null) return -1;
        return (ra - rb) * dir;
      }
      if (sort.key === 'type') {
        const t = rank(a) - rank(b);
        if (t !== 0) return t * dir;
      }
      return labelOf(a).localeCompare(labelOf(b)) * dir;
    });
  }, [rows, sort]);

  return { sortedRows, sort, cycleSort };
}

/**
 * The LocationMapPicker's checklist candidates from the business catalog: geo locations resolved
 * through the shared Location cache, radius rows mapped to the picker's form shape. Fetched only
 * while `enabled` (callers pass the dialog-open flag so the catalog loads on demand).
 */
export function useBusinessLocationCandidates(
  businessId: number | null | undefined,
  enabled: boolean,
): { businessCandidates: Location[]; radiusCandidates: RadiusFormValue[] } {
  const { data: businessLocations } = useQuery({
    ...businessLocationsQueryOptions(businessId ?? 0),
    enabled: businessId != null && enabled,
  });
  const businessCatalogIds = useMemo(
    () =>
      (businessLocations ?? [])
        .filter((l) => !l.dismissed_by_user && (l.variant === 'country' || l.variant === 'admin') && l.location_id != null)
        .map((l) => l.location_id as number),
    [businessLocations],
  );
  const { data: businessCandidates } = useQuery({
    ...locationsByIdsQueryOptions(businessCatalogIds),
    enabled: businessCatalogIds.length > 0,
  });

  const radiusCandidates = useMemo<RadiusFormValue[]>(
    () =>
      (businessLocations ?? [])
        .filter(
          (l) =>
            l.variant === 'radius' &&
            !l.dismissed_by_user &&
            l.latitude != null &&
            l.longitude != null &&
            l.radius_km != null,
        )
        .map((l) => ({
          lat: l.latitude as number,
          lng: l.longitude as number,
          radiusKm: l.radius_km as number,
          countryCode: l.country_code ?? null,
          formattedAddress: l.radius_address ?? `${l.latitude}, ${l.longitude}`,
        })),
    [businessLocations],
  );

  return { businessCandidates: businessCandidates ?? [], radiusCandidates };
}

/**
 * The merged geo+radius table — flex rows (not a DataTable) so long names truncate instead of
 * forcing a horizontal scrollbar, matching the business LocationRow style.
 */
export function LocationTargetsTable({
  sortedRows,
  sort,
  onCycleSort,
  showActions,
  onEdit,
  onDelete,
}: {
  sortedRows: LocationTargetRow[];
  sort: LocationTargetSort | null;
  onCycleSort: (key: LocationTargetSortKey) => void;
  /** false hides the actions column entirely (read-only / draft states). */
  showActions: boolean;
  onEdit: (row: LocationTargetRow) => void;
  onDelete: (row: LocationTargetRow) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
        <div className="h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <SortableHeader sortDirection={sort?.key === 'location' ? sort.dir : false} onSort={() => onCycleSort('location')}>
            Location
          </SortableHeader>
        </div>
        <div className="w-32 flex-shrink-0">
          <SortableHeader sortDirection={sort?.key === 'type' ? sort.dir : false} onSort={() => onCycleSort('type')}>
            Type
          </SortableHeader>
        </div>
        <div className="w-20 flex-shrink-0">
          <SortableHeader sortDirection={sort?.key === 'reach' ? sort.dir : false} onSort={() => onCycleSort('reach')}>
            Reach
          </SortableHeader>
        </div>
        {showActions && <div className="w-[72px] flex-shrink-0" />}
      </div>

      {sortedRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <MapPin className="mb-4 h-12 w-12 text-gray-500" strokeWidth={1} />
          <h3 className="mb-2 text-lg font-semibold text-gray-500">No Locations</h3>
          <p className="max-w-sm text-gray-500">Add locations or radius targets to define where your ads appear.</p>
        </div>
      ) : (
        sortedRows.map((r) => {
          const isRadius = r.kind === 'radius';
          const RowIcon = isRadius ? Target : MapPin;
          const label = locationTargetRowLabel(r);
          const subLabel =
            isRadius && r.lat != null && r.lng != null
              ? `${r.radiusKm} km · ${Number(r.lat).toFixed(5)}, ${Number(r.lng).toFixed(5)}`
              : isRadius
                ? `${r.radiusKm} km`
                : '';
          return (
            <div key={r.key} className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0">
              <RowIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900" title={label}>
                  {label}
                </div>
                {subLabel && <div className="truncate text-xs text-gray-500">{subLabel}</div>}
              </div>
              <div className="flex w-32 flex-shrink-0 items-center gap-1">
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    isRadius ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                  }`}
                >
                  {isRadius ? 'Radius' : 'Location'}
                </span>
              </div>
              {/* Reach — geo targets carry a catalog estimate; radius zones don't, so "-". */}
              <div className="w-20 flex-shrink-0 text-sm tabular-nums text-gray-700">
                {formatReach(r.kind === 'geo' ? r.location.reach : null)}
              </div>
              {showActions && (
                <div className="flex w-[72px] flex-shrink-0 items-center justify-end gap-0.5">
                  <IconButton aria-label={`Edit ${label}`} title="Edit" onClick={() => onEdit(r)}>
                    <Pencil className="h-4 w-4" />
                  </IconButton>
                  <IconButton variant="destructive" aria-label={`Remove ${label}`} onClick={() => onDelete(r)}>
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
